export const RETAILER_MANAGEMENT_BASE = '/retailer-management';

export type RetailerSectionId =
  | 'overview'
  | 'stores'
  | 'formats'
  | 'locations'
  | 'labels'
  | 'media'
  | 'packs'
  | 'assignments'
  | 'standards'
  | 'cycles'
  | 'events'
  | 'reporting';

export type RetailerNavCategoryId =
  | 'summary'
  | 'network'
  | 'media'
  | 'operations';

export interface RetailerSectionDefinition {
  id: RetailerSectionId;
  label: string;
  description: string;
  category: RetailerNavCategoryId;
}

export interface RetailerNavCategory {
  id: RetailerNavCategoryId;
  label: string;
}

export const RETAILER_NAV_CATEGORIES: RetailerNavCategory[] = [
  { id: 'summary', label: 'Summary' },
  { id: 'network', label: 'Network & Stores' },
  { id: 'media', label: 'Media & Configuration' },
  { id: 'operations', label: 'Operations' },
];

export const RETAILER_SECTIONS: RetailerSectionDefinition[] = [
  {
    id: 'overview',
    label: 'Overview',
    description: 'Summary of this retailer network and quick links',
    category: 'summary',
  },
  {
    id: 'stores',
    label: 'Stores',
    description: 'Manage stores in this retailer network',
    category: 'network',
  },
  {
    id: 'formats',
    label: 'Store Formats',
    description: 'Configure store formats for this retailer',
    category: 'network',
  },
  {
    id: 'locations',
    label: 'In-Store Locations',
    description: 'Manage in-store location types',
    category: 'network',
  },
  {
    id: 'labels',
    label: 'Labels',
    description: 'Manage retailer labels and tagging',
    category: 'network',
  },
  {
    id: 'media',
    label: 'Media Formats',
    description: 'Configure media formats for this retailer',
    category: 'media',
  },
  {
    id: 'packs',
    label: 'Packs',
    description: 'Manage packs and pack locking',
    category: 'media',
  },
  {
    id: 'assignments',
    label: 'Assignments',
    description: 'Assign formats and operators to stores',
    category: 'media',
  },
  {
    id: 'standards',
    label: 'Standards',
    description: 'Manage retailer standards and versions',
    category: 'media',
  },
  {
    id: 'cycles',
    label: 'Booking Cycles',
    description: 'Manage booking cycles for this retailer',
    category: 'operations',
  },
  {
    id: 'events',
    label: 'Events',
    description: 'Plan retailer events and operational context',
    category: 'operations',
  },
  {
    id: 'reporting',
    label: 'Reporting',
    description: 'Manage reporting templates and configuration',
    category: 'operations',
  },
];

export const DEFAULT_RETAILER_SECTION: RetailerSectionId = 'overview';

const SECTION_IDS = new Set<string>(RETAILER_SECTIONS.map((section) => section.id));

export function isRetailerSectionId(value: string | undefined): value is RetailerSectionId {
  return !!value && SECTION_IDS.has(value);
}

export function getRetailerSection(
  sectionId: RetailerSectionId,
): RetailerSectionDefinition | undefined {
  return RETAILER_SECTIONS.find((section) => section.id === sectionId);
}

export function retailerWorkspacePath(
  retailerId: number | string,
  section: RetailerSectionId = DEFAULT_RETAILER_SECTION,
): string {
  return `${RETAILER_MANAGEMENT_BASE}/${retailerId}/${section}`;
}

export function sectionsForCategory(categoryId: RetailerNavCategoryId): RetailerSectionDefinition[] {
  return RETAILER_SECTIONS.filter((section) => section.category === categoryId);
}

/**
 * UI-level section availability for a retailer workspace.
 * This is not a security boundary — server auth remains authoritative.
 * Return a user-facing reason to deny, or null when the section is available.
 */
export function getSectionAccessDenial(
  retailer: { id: number; name: string; archivedAt?: Date | string | null } | null,
  section: RetailerSectionId,
): string | null {
  if (!retailer) {
    return 'That retailer could not be found.';
  }

  // Internal platform users currently have access to every section.
  // When retailer grants / entitlements land, deny here with a clear reason.
  void section;
  return null;
}
